import ActivityChipTableCell from 'Components/ActivityChipTableCell/ActivityChipTableCell'
import ClickableTableCell from 'Components/ClickableTableCell'
import CustomTablePagination from 'Components/CustomTablePagination'
import EnhancedTableHead from 'Components/EnhancedTableHead'
import type { HeadCell } from 'Components/EnhancedTableHead/EnhancedTableHead'
import NoWrapTableCell from 'Components/NoWrapTableCell'
import StyledTableRow from 'Components/StyledTableRow'
import { WithLoader } from 'Components/WithLoader'
import { useAuthorityContext } from 'Services/common/Features/Authority/Context/authorityContext'
import type {
  Device,
  FetchDeviceListParams
} from 'Services/ServiceProviderManagement/Features/Device/types'
import {
  IS_WALTTI_SERVICE_ENVIRONMENT,
  PTA_PARAM_KEY,
  ROWS_PER_PAGE_DEFAULT,
  SORT_ORDER_ASC
} from 'Util/constants'
import { datetimeFormatter, showValueOrHyphen } from 'Util/formatters'
import { Paper, Table, TableBody, TableContainer } from '@mui/material'
import { useSearchParams } from 'react-router-dom'
import { t } from 'ttag'

interface DeviceListProps {
  rows: Device[]
  isLoading: boolean
  params: FetchDeviceListParams
  handleRequestSort: (event: React.MouseEvent<unknown>, property: string) => void
  handleChangePage: (event: unknown, newPage: number) => void
  count: number
  page: number
  onRowClick: (id: string, pta: string) => void
  selectedDeviceId: string | undefined
}

function DeviceList({
  rows,
  isLoading,
  params,
  handleRequestSort,
  handleChangePage,
  count,
  page,
  onRowClick,
  selectedDeviceId
}: Readonly<DeviceListProps>) {
  const [searchParams] = useSearchParams()
  const selectedPta = searchParams.get(PTA_PARAM_KEY) ?? undefined

  const { getCodespaceNameByCode } = useAuthorityContext()

  const headers: HeadCell[] = [
    {
      id: 'deviceId',
      numeric: false,
      disablePadding: false,
      label: t`Code`,
      sortable: true
    },
    {
      id: 'name',
      numeric: false,
      disablePadding: false,
      label: t`Device name`,
      sortable: true
    },
    ...(IS_WALTTI_SERVICE_ENVIRONMENT
      ? [
          {
            id: 'pta',
            numeric: false,
            disablePadding: false,
            label: t`Authority`,
            sortable: true
          }
        ]
      : []),
    {
      id: 'sales_point',
      numeric: false,
      disablePadding: false,
      label: t`Device's sales point`,
      sortable: false
    },
    {
      id: 'active',
      numeric: false,
      disablePadding: false,
      label: t`Status`,
      sortable: false
    },
    {
      id: 'createdTimestamp',
      numeric: false,
      disablePadding: false,
      label: t`Created`,
      sortable: true
    }
  ]

  const orderPrefix = `${params.sort_order === SORT_ORDER_ASC ? '-' : ''}`
  const orderBy = `${orderPrefix}${params.sort_by}`

  return (
    <WithLoader loading={isLoading} rows={ROWS_PER_PAGE_DEFAULT} height={33}>
      <Paper sx={{ width: '100%', mb: 2 }}>
        <TableContainer>
          <Table sx={{ minWidth: 750 }} aria-labelledby="tableTitle" size="small">
            <EnhancedTableHead
              orderBy={orderBy}
              headCells={headers}
              onRequestSort={handleRequestSort}
            />
            <TableBody>
              {rows.map(row => {
                const codespaceName = getCodespaceNameByCode(row.pta)
                return (
                  <StyledTableRow
                    hover
                    tabIndex={-1}
                    key={row.device_id}
                    selected={selectedDeviceId === row.device_id && row.pta === selectedPta}
                  >
                    <ClickableTableCell onClick={() => onRowClick(row.device_id, row.pta)}>
                      {showValueOrHyphen(row.device_id)}
                    </ClickableTableCell>
                    <ClickableTableCell onClick={() => onRowClick(row.device_id, row.pta)}>
                      {showValueOrHyphen(row.name)}
                    </ClickableTableCell>
                    {IS_WALTTI_SERVICE_ENVIRONMENT && (
                      <NoWrapTableCell title={codespaceName}>{codespaceName}</NoWrapTableCell>
                    )}
                    <NoWrapTableCell>{showValueOrHyphen(row.sales_point?.name)}</NoWrapTableCell>
                    <ActivityChipTableCell
                      active={row.active}
                      activity_end_date={row.activity_end_date}
                    />
                    <NoWrapTableCell
                      title={datetimeFormatter(row.created_timestamp)}
                      component="th"
                      scope="row"
                    >
                      {datetimeFormatter(row.created_timestamp)}
                    </NoWrapTableCell>
                  </StyledTableRow>
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <CustomTablePagination count={count} page={page} onPageChange={handleChangePage} />
      </Paper>
    </WithLoader>
  )
}

export default DeviceList
